import { useEffect, useState } from "react";
import type { PageSize } from "@/components/data-grid/ListPager";

/**
 * Pages an array that is already loaded and filtered on the client. The
 * small lists (Clients, COI, Capital Providers) fit in one request, so this
 * gives them the same footer as the paged lists without a server round trip.
 */
export function usePagedRows<T>(rows: T[], defaultSize: PageSize = 50) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(defaultSize);
  // A filter change is visible as a change in how many rows there are; the
  // clamp below covers the rest.
  useEffect(() => { setPage(1); }, [rows.length, pageSize]);

  const total = rows.length;
  const size = pageSize === "all" ? Math.max(total, 1) : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const current = Math.min(page, totalPages);
  const visible = pageSize === "all" ? rows : rows.slice((current - 1) * size, current * size);

  return {
    rows: visible, total, page: current, totalPages, pageSize, setPageSize,
    hasPrev: pageSize !== "all" && current > 1,
    hasNext: pageSize !== "all" && current < totalPages,
    prev: () => setPage((p) => Math.max(1, p - 1)),
    next: () => setPage((p) => Math.min(totalPages, p + 1)),
  };
}
